import React, { useEffect, useMemo, useState } from 'react'
import useTrans from '@hooks/useTrans'
import { Alert, Button, EditorImage, EditorText, FormField, FormSelect } from '@components/atoms'
import { AlertTriangle, Checkmark, ChevronDown } from '@components/icons'
import { cn } from '@utils/safeTs'
import styles from './EditorRiepilogo.module.sass'
import {
  EditorColorType,
  EditorErrorType,
  EditorImageType,
  EditorTextType,
  NumeroColoriType,
  PosizioneType,
} from '@utils/types'
import { convertColorToHex } from '@utils/editor-bozze'

interface Props {
  selectedPosizione: PosizioneType & { images?: EditorImageType[]; texts?: EditorTextType[] }
  numeriColori?: NumeroColoriType[]
  coloriRilevati?: EditorColorType[]
  errors?: EditorErrorType[]
  onChange?: (value: number) => void
  isNumeroColoriEditable?: boolean
  className?: string
}

const EditorRiepilogo = (props: Props) => {
  const {
    selectedPosizione,
    numeriColori = [],
    coloriRilevati = [],
    errors = [],
    onChange,
    isNumeroColoriEditable = true,
    className = '',
  } = props

  const t = useTrans()
  const [open, setOpen] = useState(false)
  const [localValue, setLocalValue] = useState(
    selectedPosizione?.configurazione?.numero_colori?.id?.toString()
  )

  const numeroColoriError = useMemo(() => {
    return errors?.find((e) => e.key === `numero-colori-${selectedPosizione?.id}`)
  }, [errors])
  const statusRiepilogo = numeroColoriError?.variant || 'success'

  useEffect(() => {
    setLocalValue(selectedPosizione?.configurazione?.numero_colori?.id?.toString())
  }, [selectedPosizione])

  useEffect(() => {
    // di default accordion aperto se ci sono errori, altrimenti chiuso
    if (numeroColoriError) {
      setOpen(true)
    } else {
      setOpen(false)
    }
  }, [numeroColoriError?.key])

  return (
    <div className={cn(styles.root, styles[`root--${statusRiepilogo}`], className)}>
      {!selectedPosizione?.configurazione?.numero_colori?.full_color && (
        <FormField
          id="editorriepilogo_numero-colori"
          label={t('Numero colori')}
          styleVariant="editor"
          className={styles.field}
        >
          <FormSelect
            id="editorriepilogo_numero-colori"
            name="numeroColori"
            value={localValue}
            options={numeriColori.map((n) => ({
              label: n.numero_colori?.toString() || '0',
              value: n.id?.toString(),
            }))}
            className={styles.select}
            isDisabled={!isNumeroColoriEditable}
            onChange={(e) => {
              setLocalValue(e.target.value)
              if (onChange) onChange(parseInt(e.target.value))
            }}
          />
        </FormField>
      )}

      <div className={cn(styles.accordion, open ? styles['accordion--open'] : '')}>
        <div className={styles.accordionTop}>
          <div className={styles.info}>
            <span className={styles.infoLabel}>
              {t('Colori rilevati: {0}', [coloriRilevati.length?.toString()])}
            </span>
            <span className={styles.infoIcon}>
              {statusRiepilogo === 'success' ? <Checkmark /> : <AlertTriangle />}
            </span>
          </div>
          {!!coloriRilevati?.length && (
            <button
              type="button"
              className={styles.accordionTrigger}
              onClick={() => setOpen((prev) => !prev)}
            >
              <span>{t('Dettagli')}</span>
              <ChevronDown />
            </button>
          )}
        </div>
        {!!coloriRilevati?.length && (
          <div className={styles.accordionContent}>
            {coloriRilevati.map((c) => (
              <div className={styles.item} key={c.value}>
                <div
                  className={styles.colorBox}
                  style={{ background: convertColorToHex(c.variant, c.value) }}
                />
                <div className={styles.list}>
                  {c.images?.map((i) => (
                    <EditorImage key={i.id} image={i} size="small" />
                  ))}
                  {c.texts?.map((t) => (
                    <EditorText key={t.id} text={t} size="small" />
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}

export default EditorRiepilogo
